import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  message,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import PfModal from './PfModal';
import { showSuccess, showError } from '../../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';

const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));

interface Pf {
  pf_id: number;
  pf_name: string;
  pf_margin: number;
  pf_status: number;
}

const Pf: React.FC = () => {
  const [pfs, setPfs] = useState<Pf[]>([]);
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<Pf | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});


  const cancel: PopconfirmProps['onCancel'] = (e) => {
    console.log(e);
    showError('Click on No');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchPf = async () => {
    try {
      const data = await apiRequest<Pf[]>({
        method: 'get',
        url: 'price-formulas',
      });
      setPfs(data);
    } catch (error) {
      showError('Failed to fetch Price Formulas');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchPf();
  }, []);

  const handleEditClick = (record: Pf) => {
    setIsEditMode(true);
    setInitialValues({
      pf_id: record.pf_id,
      pf_name: record.pf_name,
      pf_margin: record.pf_margin,
      pf_status: record.pf_status,
    });
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchPf();
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (pf_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-price-formula/${pf_id}`,
      });
      showSuccess('Price Formula deleted');
      fetchPf();
    } catch {
      showError('Failed to delete Price Formula');
    }
  };

  const handleStatusToggle = async (pfId: number) => {
    setPfs((prev) =>
      prev.map((item) =>
        item.pf_id === pfId
          ? { ...item, pf_status: item.pf_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-price-formula/${pfId}`,
      });
      showSuccess('Price Formula status updated');
    } catch (error) {
      showError('Failed to update status');
      setPfs((prev) =>
        prev.map((item) =>
          item.pf_id === pfId
            ? { ...item, pf_status: item.pf_status === 1 ? 0 : 1 }
            : item
        )
      );
    }
  };

  const handleTableChange: TableProps<Pf>['onChange'] = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };




  const columns: TableColumnsType<Pf> = [
    {
      title: 'Price Formula',
      dataIndex: 'pf_name',
      key: 'pf_name',
      filters: Array.from(new Set(pfs.map((s) => s.pf_name))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.pf_name || null,
      onFilter: (value, record) => record.pf_name.includes(value as string),
      sorter: (a, b) => a.pf_name.localeCompare(b.pf_name),
      sortOrder: sortedInfo.columnKey === 'pf_name' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div>{record.pf_name}</div>,
    },
    {
      title: 'Margin (%)',
      dataIndex: 'pf_margin',
      key: 'pf_margin',
      filteredValue: filteredInfo.pf_margin || null,

      // Filter logic (convert both sides to numbers)
      onFilter: (value, record) => Number(record.pf_margin) === Number(value),

      // Sort numerically
      sorter: (a, b) => Number(a.pf_margin) - Number(b.pf_margin),
      sortOrder: sortedInfo.columnKey === 'pf_margin' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,

      // Create unique filters, cast to number
      filters: Array.from(new Set(pfs.map((s) => Number(s.pf_margin)))).map((margin) => ({
        text: `${margin.toFixed(2)}%`,
        value: margin,
      })),

      // Render the margin with two decimal places
      render: (text, record) => {
        const margin = Number(record.pf_margin);
        return <span>{!isNaN(margin) ? `${margin.toFixed(2)}%` : '—'}</span>;
      },
    }


  ];


  // Conditionally add action column
  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 200,
      render: (_: any, record: Pf) => (
        <Space size="middle">
          {currentModule?.right_edit === 'Y' && (
            <>
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                checked={!!record.pf_status}
                onChange={() => handleStatusToggle(record.pf_id)}
              />
              <Button onClick={() => handleEditClick(record)} className="px-8">
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Popconfirm
              title="Delete this Price-Formula?"
              onConfirm={() => handleDelete(record.pf_id)}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              <Button danger className="px-8">
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }


  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Price Formula</Breadcrumb.Item>
        </Breadcrumb>

        {currentModule?.right_add === 'Y' && (
          <Flex gap="small">
            <Button type="primary" onClick={showCreateModal}>
              Add Price Formula
            </Button>
          </Flex>
        )}
      </Space>


      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<Pf>
          tableLayout="auto"
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          onChange={handleTableChange}
          rowKey="pf_id"
          dataSource={pfs.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          bordered
          pagination={false}
          scroll={{ y: 37 * 13 }}
          locale={{ emptyText: 'No Price Formula found' }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, pfs.length)} of{' '}
                {pfs.length}
              </span>
              <Pagination
                {...pagination}
                total={pfs.length}
                showSizeChanger
                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
              />
            </Flex>
          )}
        />
      </div>

      <PfModal
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
      />
    </>
  );
};

export default Pf;